import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
//
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
//
import Box from "@mui/material/Box";
import { styled } from "@material-ui/styles";
// component, hooks
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import useLocaleRoute from "../../../../common/hooks/useLocaleRoute";
import useModulePublic from "../../../hooks/useModulePublic";
// store
import { StoreContext } from "../../../../store/StoreProvider";
import Act from "../../../../store/actions";
// config,utils
import { ROUTE_PATH, ROUTE_NAME } from "../../../../config/CONSTANTS";
import urls from "../../../../api/project/ApiUrls";
import featureDisplay from "../../../../common/utils/featureDisplay";
import InputPlusIcon from "../../../../common/components/input/InputPlusIcon";
import IconEmail from "../../../../../images/icon_email_active/icon_email_active.svg";
import IconPassword from "../../../../../images/icon_password_active/icon_password_active.svg";
import ButtonMain from "../../../../common/components/button/ButtonMain";
import { validateEmail } from "../../../../common/utils/validator";
import fireAuth from "../../../../api/firebase/auth/utils";
import { setCommonDialogContext } from "../../../../reduxStore/commonSlice";

// style
const StyledBox = styled(
  Box,
  {}
)(({ theme }) => ({
  "&:hover": {
    cursor: "pointer",
  },
}));

const StyledLinkDark = styled(
  "span",
  {}
)(({ theme }) => ({
  color: "#121212",
  cursor: "pointer",
}));

const StyledLinkBlue = styled(
  "span",
  {}
)(({ theme }) => ({
  color: "#3bbff0",
  cursor: "pointer",
}));

const StyledHint = styled(
  "span",
  {}
)(({ theme }) => ({
  color: "#ff5252",
}));

const EmailLogin = () => {
  // props

  // store
  const reduxDispatch = useDispatch();

  // route,intl
  const intl = useIntl();
  const navigate = useNavigate();
  // store
  const [state, dispatch] = useContext(StoreContext);
  const { user } = state;
  const { locale } = user;
  // local state
  const [email, setEmail] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [password, setPassword] = useState("");
  const [passwordHint, setPasswordHint] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const emailValidate = validateEmail(email);
  const passwordValidate = !!password;
  const disabledSubmit = !(emailValidate && passwordValidate);

  // hooks
  const { handleLocaleRoute } = useLocaleRoute(
    locale,
    urls?.locale_lang?.LOCALE_DEFAULT
  );

  const handleEmailChange = (e) => {
    const emailVal = e.target.value;
    setEmail(emailVal);
    // emailReplace()
  };
  const handlePassWordChange = (e) => {
    const passwordVal = e.target.value;
    setPassword(passwordVal);
  };
  const handleSubmit = () => {
    if (!disabledSubmit) {
      fireAuth.signIn({
        email,
        password,
        onSuccess: ({ user: loginUser }) => {
          setEmailHint("");
          setPasswordHint("");
          const { displayName: userName } = loginUser;
          dispatch({
            type: Act.COMMON_SET_MESSAGE,
            payload: {
              open: true,
              content: intl.formatMessage(
                {
                  id: "login.with.email.success.message",
                  defaultMessage: "{name}, 你已成功登入",
                },
                {
                  name: userName,
                }
              ),
            },
          });
          // redirect to sna page
          navigate(handleLocaleRoute(ROUTE_PATH.Console));
        },
        onError: ({ errorCode, errorMessage }) => {
          if (
            [
              "auth/invalid-email",
              "'auth/user-not-found'",
              "auth/wrong-password",
              "auth/user-disabled",
            ].includes(errorCode)
          ) {
            setEmailHint("輸入不正確");
            setPasswordHint("輸入不正確");
          }

          dispatch({
            type: Act.COMMON_SET_ERROR_MESSAGE,
            payload: {
              open: true,
              content: intl.formatMessage({
                id: "login.with.email.failure.message",
                defaultMessage: "登入失敗，請重新登入",
              }),
            },
          });
        },
      });
    }
  };
  const handleDisplayPassword = (e) => {
    const checkedVal = e.target.checked;
    setShowPassword(checkedVal);
  };
  const handleForgetPassword = () => {
    // check email 符合格式
    if (!emailValidate) return;

    const onSuccess = () => {
      reduxDispatch(
        setCommonDialogContext({
          openSignal: Date.now(),
          title: "已傳送電子郵件",
          contentText: [
            `我們已傳送一封電子郵件到 ${email}，`,
            `請前往信箱收信並重設密碼。`,
          ],
          yesText: "好",
          withFinishIcon: true,
        })
      );
    };
    const onError = ({ errorCode, errorMessage }) => {
      reduxDispatch(
        setCommonDialogContext({
          openSignal: Date.now(),
          title: "寄送電子郵件失敗",
          contentText: [`請確認電子郵件正確性`, errorMessage],
          yesText: "好",
        })
      );
    };
    fireAuth.sendPasswordResetEmail({ email, onSuccess, onError });
  };

  return (
    <Box>
      <Box mb={2}>
        <InputPlusIcon
          // label="Email"
          placeholder={intl.formatMessage({
            id: "login.email",
            defaultMessage: "電子郵件",
          })}
          startAdornmentIcon={<img src={IconEmail} alt="icon email" />}
          onChange={handleEmailChange}
          error={!!(email && !emailValidate)}
        />
        {emailHint && (
          <Box mt={1}>
            <Typography variant="body2">
              <StyledHint>{emailHint}</StyledHint>
            </Typography>
          </Box>
        )}
      </Box>
      <Box mb={2}>
        <InputPlusIcon
          // label="Password"
          placeholder={intl.formatMessage({
            id: "login.password",
            defaultMessage: "密碼",
          })}
          type={showPassword ? "text" : "password"}
          startAdornmentIcon={<img src={IconPassword} alt="icon password" />}
          onChange={handlePassWordChange}
        />
        {passwordHint && (
          <Box mt={1}>
            <Typography variant="body2">
              <StyledHint>{passwordHint}</StyledHint>
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        mb={2}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
      >
        <Box>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showPassword}
                  onChange={handleDisplayPassword}
                />
              }
              label={intl.formatMessage({
                id: "login.display.password",
                defaultMessage: "顯示密碼",
              })}
            />
          </FormGroup>
        </Box>
        <Box display="flex" alignItems="center" onClick={handleForgetPassword}>
          <Typography textAlign="center" onClick={handleForgetPassword}>
            <StyledLinkDark
              style={{
                color: email && emailValidate ? "#121212" : "#909090",
                "&:hover": {
                  cursor: emailValidate ? "pointer" : "inherit",
                },
              }}
            >
              <FormattedMessage
                id="login.forget.password"
                defaultMessage="忘記密碼"
              />
            </StyledLinkDark>
          </Typography>
        </Box>
      </Box>
      <Box mb={8}>
        <ButtonMain
          buttonProps={{
            text: intl.formatMessage({
              id: "login.submit",
              defaultMessage: "登入",
            }),
            variant: disabledSubmit ? "disabled" : "gradient",
            fullWidth: true,
            disabled: disabledSubmit,
            onClick: handleSubmit,
          }}
        />
      </Box>
    </Box>
  );
};

EmailLogin.propTypes = {};

EmailLogin.defaultProps = {};

export default EmailLogin;
